import Cookies from 'js-cookie'

// src/services/api.js

const apiUri = (process.env.VUE_APP_API_URL || 'https://api.nocodeagent.ai').replace(/\/$/, '')

const BASE_URL =  apiUri + '/api'
export const API_BASE_URL = apiUri

// ===== Helper functions =====
async function request(path, options = {}) {
  try {
    const res = await fetch(BASE_URL + path, options)
    return handleResponse(res)
  } catch (error) {
    if (error?.message && error.message !== 'Failed to fetch') {
      throw error
    }

    throw new Error(`Unable to reach NoCodeAgent API at ${apiUri}.`)
  }
}

async function get(path) {
  const token = Cookies.get('token')
  return request(path, {
    method: 'GET',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  })
}

async function post(path, body) {
  const token = Cookies.get('token')
  return request(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify(body)
  })
}

async function put(path, body) {
  const token = Cookies.get('token')
  return request(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify(body)
  })
}

async function patch(path, body) {
  const token = Cookies.get('token')
  return request(path, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    body: JSON.stringify(body)
  })
}

async function del(path) {
  const token = Cookies.get('token')
  return request(path, {
    method: 'DELETE',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  })
}

async function handleResponse(res) {
  const text = await res.text()
  let data = {}

  try {
    data = text ? JSON.parse(text) : {}
  } catch (_) {
    data = { message: text }
  }

  if (!res.ok) {
    throw new Error(data?.error || data?.message || 'Erreur API')
  }

  return data
}

// === Booking (Prise de rendez-vous) ===
export const booking = {
  getByAgent: (agentId) => get(`/booking/${agentId}`),
  getBookingsByAgent: (agentId) => get(`/booking/${agentId}/bookings`),
  updateBooking: (id, data) => patch(`/booking/bookings/${id}`, data),
  save: (data) => post('/booking', data)
}

// === Auth ===
export const auth = {
  register: (data) => post('/auth/register', data),
  login: (data) => post('/auth/login', data),
  status: () => post('/auth/status', {})
}

// === Agents ===
export const agents = {
  getAll: () => get('/agents'),
  getById: (id) => get(`/agents/${id}`),
  create: (data) => post('/agents', data),
  importPublicDraft: (data) => post('/agents/import-public-draft', data),
  update: (id, data) => put(`/agents/${id}`, data),
  delete: (id) => del(`/agents/${id}`),
  coach: {
    context: (id) => get(`/agents/${id}/coach/context`),
    message: (id, data) => post(`/agents/${id}/coach/messages`, data),
    propose: (id, data) => post(`/agents/${id}/coach/propose`, data),
    apply: (id, data) => post(`/agents/${id}/coach/apply`, data)
  },
  modules: {
    get: (id) => get(`/agents/${id}/modules`),
    add: (id, data) => post(`/agents/${id}/modules`, data),
    update: (id, moduleId, data) => put(`/agents/${id}/modules/${moduleId}`, data),
    delete: (id, moduleId) => del(`/agents/${id}/modules/${moduleId}`)
  }
}

// === Actions ===
export const actions = {
  getAll: () => get('/actions'),
  getById: (id) => get(`/actions/${id}`),
  create: (data) => post('/actions', data),
  update: (id, data) => put(`/actions/${id}`, data),
  delete: (id) => del(`/actions/${id}`)
}

// === Chat ===
export const chat = {
  send: (sessionId) => get(`/chat/${sessionId}`),
  sendPublic: (sessionId) => get(`/chat/public/${sessionId}`),
  auto: (data) => post('/chat/public/auto', data)
}

const templatesStorageKey = 'nocodeagent_message_templates'

function readLocalTemplates() {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(templatesStorageKey)
    return raw ? JSON.parse(raw) : []
  } catch (_) {
    return []
  }
}

function writeLocalTemplates(rows) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(templatesStorageKey, JSON.stringify(rows))
}

// Message templates do not have a backend route yet. Keep the screen usable and
// warning-free with local persistence until a server endpoint is added.
export const templates = {
  getAll: async () => readLocalTemplates(),
  create: async (data) => {
    const rows = readLocalTemplates()
    const template = {
      id: Date.now(),
      name: data.name,
      type: data.type,
      subject: data.subject || '',
      content: data.content || ''
    }
    writeLocalTemplates([template, ...rows])
    return template
  },
  update: async (id, data) => {
    const rows = readLocalTemplates()
    const updated = rows.map(item => String(item.id) === String(id) ? { ...item, ...data } : item)
    writeLocalTemplates(updated)
    return updated.find(item => String(item.id) === String(id)) || null
  },
  delete: async (id) => {
    writeLocalTemplates(readLocalTemplates().filter(item => String(item.id) !== String(id)))
    return { message: 'Template deleted' }
  }
}

// === Connected Channels ===
export const connectedChannels = {
  getAll: () => get('/connected-channels'),
  getById: (id) => get(`/connected-channels/${id}`),
  create: (data) => post('/connected-channels', data),
  update: (id, data) => put(`/connected-channels/${id}`, data),
  delete: (id) => del(`/connected-channels/${id}`)
}

// === Contexts ===
export const contexts = {
  getAll: () => get('/contexts'),
  getById: (id) => get(`/contexts/${id}`),
  create: (data) => post('/contexts', data),
  update: (id, data) => put(`/contexts/${id}`, data),
  delete: (id) => del(`/contexts/${id}`)
}

// === Conversation History ===
export const conversationHistory = {
  getAll: () => get('/conversation-history'),
  getById: (id) => get(`/conversation-history/${id}`),
  getBySession: (sessionId) => get(`/conversation-history?session_id=${sessionId}`),
  create: (data) => post('/conversation-history', data),
  update: (id, data) => put(`/conversation-history/${id}`, data),
  delete: (id) => del(`/conversation-history/${id}`)
}

// === Entities ===
export const entities = {
  getAll: () => get('/entities'),
  getById: (id) => get(`/entities/${id}`),
  create: (data) => post('/entities', data),
  update: (id, data) => put(`/entities/${id}`, data),
  delete: (id) => del(`/entities/${id}`)
}

// === Flows ===
export const flows = {
    getAll: (agentId) => {
      if (!agentId) return get('/flows');
      return get(`/flows?agent_id=${encodeURIComponent(agentId)}`);
    },
  getById: (id) => get(`/flows/${id}`),
  create: (data) => post('/flows', data),
  update: (id, data) => put(`/flows/${id}`, data),
  simulate: (id, data) => post(`/flows/${id}/simulate`, data),
  delete: (id) => del(`/flows/${id}`)
}

// === Integrations ===
export const integrations = {
  getAll: () => get('/integrations'),
  getById: (id) => get(`/integrations/${id}`),
  create: (data) => post('/integrations', data),
  update: (id, data) => put(`/integrations/${id}`, data),
  delete: (id) => del(`/integrations/${id}`)
}

// === Intents ===
export const intents = {
  getAll: () => get('/intents'),
  getById: (id) => get(`/intents/${id}`),
  create: (data) => post('/intents', data),
  update: (id, data) => put(`/intents/${id}`, data),
  delete: (id) => del(`/intents/${id}`)
}

// === Invoices ===
export const invoices = {
  getAll: () => get('/invoices'),
  getById: (id) => get(`/invoices/${id}`),
  create: (data) => post('/invoices', data),
  update: (id, data) => put(`/invoices/${id}`, data),
  delete: (id) => del(`/invoices/${id}`)
}

// === Knowledge Bases ===
export const knowledgeBases = {
  getAll: (agent_id) => get(`/knowledge-bases?agent_id=${agent_id}`),
  getById: (id) => get(`/knowledge-bases/${id}`),
  create: (data) => post('/knowledge-bases', data),
  update: (id, data) => put(`/knowledge-bases/${id}`, data),
  delete: (id) => del(`/knowledge-bases/${id}`)
}

// === Personality Profiles ===
export const personalityProfiles = {
  getAll: (id) => get(`/personality-profiles?agent_id=${id}`),
  getById: (id) => get(`/personality-profiles/${id}`),
  create: (data) => post('/personality-profiles', data),
  update: (id, data) => put(`/personality-profiles/${id}`, data),
  delete: (id) => del(`/personality-profiles/${id}`)
}

// === Sessions ===
export const sessions = {
  getAll: (agent_id) => get(`/sessions?agent_id=${agent_id}`),
  getById: (id) => get(`/sessions/${id}`),
  create: (data) => post('/sessions', data),
  update: (id, data) => put(`/sessions/${id}`, data),
  delete: (id) => del(`/sessions/${id}`)
}

// === Live Monitor ===
export const liveMonitor = {
  getSessions: ({ agentId, page = 1, limit = 8, filter = 'all', sort = 'newest', date = '' }) => {
    const params = new URLSearchParams()
    if (agentId) params.set('agent_id', agentId)
    params.set('page', String(page))
    params.set('limit', String(limit))
    params.set('filter', filter)
    params.set('sort', sort)
    if (date) params.set('date', date)
    return get(`/live-monitor/sessions?${params.toString()}`)
  },
  getSessionById: (sessionId) => get(`/live-monitor/sessions/${sessionId}`)
}

// === Support Tickets ===
export const supportTickets = {
  getAll: () => get('/support_tickets'),
  getById: (id) => get(`/support_tickets/${id}`),
  create: (data) => post('/support_tickets', data),
  update: (id, data) => put(`/support_tickets/${id}`, data),
  delete: (id) => del(`/support_tickets/${id}`)
}

// === Triggers ===
export const triggers = {
  getAll: () => get('/triggers'),
  getById: (id) => get(`/triggers/${id}`),
  create: (data) => post('/triggers', data),
  update: (id, data) => put(`/triggers/${id}`, data),
  delete: (id) => del(`/triggers/${id}`)
}

// === Variables ===
export const variables = {
  getAll: () => get('/variables'),
  getById: (id) => get(`/variables/${id}`),
  create: (data) => post('/variables', data),
  update: (id, data) => put(`/variables/${id}`, data),
  delete: (id) => del(`/variables/${id}`)
}

// === Voice (TTS & STT) ===
export const voice = {
  tts: (data) => post('/voice/tts', data),
  stt: (data) => post('/voice/stt', data)
}

// === Voice Catalog ===
export const voiceCatalog = {
  getAll: (params = {}) => {
    const query = new URLSearchParams()
    if (params.language) query.set('language', params.language)
    if (params.search) query.set('search', params.search)
    if (params.accent) query.set('accent', params.accent)
    if (params.locale) query.set('locale', params.locale)
    if (params.gender) query.set('gender', params.gender)
    if (params.age) query.set('age', params.age)
    if (params.category) query.set('category', params.category)
    if (params.use_case) query.set('use_case', params.use_case)
    if (params.descriptive) query.set('descriptive', params.descriptive)
    if (params.provider_name) query.set('provider_name', params.provider_name)
    if (params.page) query.set('page', String(params.page))
    if (params.limit) query.set('limit', String(params.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return get(`/voice-catalog${suffix}`)
  },
  getLanguages: () => get('/voice-catalog/languages'),
  getById: (id) => get(`/voice-catalog/${id}`),
  sync: (data) => post('/voice-catalog/sync', data)
}

// === Phone Numbers / Telephony ===
export const phoneNumbers = {
  getAll: () => get('/phone-numbers'),
  search: (data) => post('/phone-numbers/search', data),
  purchase: (data) => post('/phone-numbers/purchase', data),
  connect: (data) => post('/phone-numbers/connect', data),
  associate: (data) => post('/phone-numbers/associate', data),
  update: (id, data) => put(`/phone-numbers/${id}`, data),
  delete: (id, release = false) => del(`/phone-numbers/${id}${release ? '?release=true' : ''}`)
}

// === Driver ===
export const driver = {
  get: () => get('/driver/get'),
  update: (data) => put('/driver/edit', data),
  delete: () => del('/driver/delete')
}

// === Analytics ===
export const analytics = {
  forAgent: (id) => get(`/analytics/agents/${id}`),
  forUsers: () => get('/analytics/users'),
}


export const publicAgentDrafts = {
  get: (draftId) => get(`/public-agent-drafts/${draftId}`),
  create: (data) => post('/public-agent-drafts', data)
}
